import React, { useId, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { NIDATA_LOADING_LABEL } from '../lib/nidataUiCopy';
import {
  hitMatchesQuery,
  isAllowedSearchHref,
  isInAppSearchHref,
  type SiteSearchGroup,
  type SiteSearchHit,
} from '../lib/searchText';

const GROUPS: readonly SiteSearchGroup[] = ['หน้า', 'หน่วยงาน', 'แบบฟอร์ม'];

export type SiteSearchComboboxProps = {
  hits: readonly SiteSearchHit[];
  placeholder: string;
  ariaLabel: string;
  departmentsLoading?: boolean;
  onActivateHit?: (hit: SiteSearchHit) => void;
};

export function SiteSearchCombobox({
  hits,
  placeholder,
  ariaLabel,
  departmentsLoading = false,
  onActivateHit,
}: SiteSearchComboboxProps): React.ReactElement {
  const navigate = useNavigate();
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const closingRef = useRef(false);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const visibleHits = useMemo(
    () => hits.filter((hit) => isAllowedSearchHref(hit.href) && hitMatchesQuery(hit, query)),
    [hits, query],
  );

  const showList = open && query.trim().length >= 2;
  const showDepartmentLoading = showList && departmentsLoading;
  const showEmpty = showList && visibleHits.length === 0 && !showDepartmentLoading;
  const activeHit = visibleHits[activeIndex] ?? visibleHits[0];

  const closeList = (): void => {
    closingRef.current = true;
    setOpen(false);
    inputRef.current?.focus();
    closingRef.current = false;
  };

  const followHit = (hit: SiteSearchHit): void => {
    if (hit.departmentId && onActivateHit) {
      onActivateHit(hit);
    } else if (isInAppSearchHref(hit.href)) {
      navigate(hit.href);
    } else {
      window.open(hit.href, '_blank', 'noopener,noreferrer');
    }
    setQuery('');
    setOpen(false);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!showList || visibleHits.length === 0) return;
      setActiveIndex((current) => {
        const next = event.key === 'ArrowDown' ? current + 1 : current - 1;
        if (next < 0) return visibleHits.length - 1;
        if (next >= visibleHits.length) return 0;
        return next;
      });
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      closeList();
      return;
    }
    if (event.key === 'Enter') {
      if (!showList || !activeHit) return;
      event.preventDefault();
      followHit(activeHit);
    }
  };

  return (
    <div className="relative min-w-0 flex-1">
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-label={ariaLabel}
        aria-expanded={showList}
        aria-controls={listId}
        aria-activedescendant={showList && activeHit ? `site-search-${activeHit.id}` : undefined}
        aria-autocomplete="list"
        placeholder={placeholder}
        value={query}
        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
          setActiveIndex(0);
        }}
        onFocus={() => {
          if (closingRef.current) return;
          setOpen(true);
        }}
        onKeyDown={onKeyDown}
      />
      {showEmpty ? (
        <p className="absolute left-0 right-0 top-full z-20 mt-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600 shadow-md" aria-live="polite">
          ไม่พบรายการ
        </p>
      ) : null}
      {showList && (visibleHits.length > 0 || showDepartmentLoading) ? (
        <ul
          id={listId}
          role="listbox"
          aria-label={ariaLabel}
          className="absolute left-0 right-0 top-full z-20 mt-1 max-h-80 overflow-y-auto rounded-lg border border-slate-200 bg-white py-1 shadow-md"
        >
          {GROUPS.map((group) => {
            const groupHits = visibleHits.filter((hit) => hit.group === group);
            const showGroup = groupHits.length > 0 || (group === 'หน่วยงาน' && showDepartmentLoading);
            if (!showGroup) return null;
            return (
              <li key={group} role="group" aria-label={group} className="px-1 py-1">
                <div className="px-2 py-1 text-xs font-semibold text-slate-500">{group}</div>
                {group === 'หน่วยงาน' && showDepartmentLoading ? (
                  <div className="px-2 py-2 text-sm text-slate-600">{NIDATA_LOADING_LABEL}</div>
                ) : (
                  groupHits.map((hit) => {
                    const selected = hit.id === activeHit?.id;
                    const optionClass = `block rounded-md px-2 py-2 text-sm text-slate-900 ${selected ? 'bg-teal-50' : ''}`;
                    const body = (
                      <>
                        <span className="block font-medium">{hit.label}</span>
                        {hit.hint ? <span className="block text-xs text-slate-500">{hit.hint}</span> : null}
                      </>
                    );
                    return isInAppSearchHref(hit.href) ? (
                      <Link
                        key={hit.id}
                        id={`site-search-${hit.id}`}
                        role="option"
                        aria-selected={selected}
                        to={hit.href}
                        className={optionClass}
                        onClick={(event) => {
                          if (hit.departmentId && onActivateHit) {
                            event.preventDefault();
                            followHit(hit);
                            return;
                          }
                          setQuery('');
                          setOpen(false);
                        }}
                      >
                        {body}
                      </Link>
                    ) : (
                      <a
                        key={hit.id}
                        id={`site-search-${hit.id}`}
                        role="option"
                        aria-selected={selected}
                        href={hit.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={optionClass}
                        onClick={() => {
                          setQuery('');
                          setOpen(false);
                        }}
                      >
                        {body}
                      </a>
                    );
                  })
                )}
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
